/**
 * useAlertedSentries - an attack alerts whoever the GM had set Unaware (2026-10-21, the user's ruling).
 *
 * A hand-set Unaware now counts at every rung (engine/stealth/ladder `isUnawareIn`), so without this a sentry set
 * Unaware could be taken down again and again. Struck - hit, missed, a body action; never a jam, which reached
 * nobody - they now search (`alertStruck`), as the `Unw` condition is taken away by an attack. Written by whoever
 * directs: the ladder is theirs alone (`stealth.apply` does nothing elsewhere, and nothing when nobody changed).
 *
 * READ OFF THE LOGS, not off the display's impacts, which live 2.5 s: a director's screen that heard of an attack
 * late (a slow sync, a tab in the background) must still alert. The logs are read by high-water marks
 * (engine/impacts `collectImpacts`), so a line is read once; a PocketBase reseed or a page load re-reading history
 * is answered by keeping only attacks made since this screen opened.
 *
 * Left for the record (2026-10-21): the player's own screen still calls the sentry unaware until the director's
 * write comes back - the round trip of a log line and a frame - since only the director writes the ladder.
 *
 * @param {object} s - tabs, firestoreNarrative and roster (what the impacts are read from), and the `stealth`
 *   (hooks/useShellStealth)
 */
import { useEffect, useRef } from 'react';
import { collectImpacts } from '../engine/impacts';
import { alertStruck } from '../engine/stealth/ladder';

/** What reached its target: every impact an attack makes but a jam (nothing fired) and an emergence (no attack). */
const STRIKES = new Set(['hit', 'partial', 'deflected', 'blocked', 'miss', 'action']);

const FIRST_MARKS = { local: {}, shared: 0 };

export default function useAlertedSentries({ tabs, firestoreNarrative, roster, stealth }) {
  const marks = useRef(FIRST_MARKS);
  const openedAt = useRef(Date.now());
  const apply = stealth?.apply;
  useEffect(() => {
    if (!apply) return;
    const read = collectImpacts(tabs, firestoreNarrative ?? [], roster, marks.current, Date.now());
    marks.current = read.marks;
    const struck = read.impacts
      .filter((i) => STRIKES.has(i.outcome) && i.at >= openedAt.current)
      .map((i) => i.combatantId);
    if (struck.length) apply((st) => alertStruck(st, struck));
  }, [tabs, firestoreNarrative, roster, apply]);
}
